import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Link } from 'react-router-dom';
import { CircularProgress, Box, useMediaQuery } from '@mui/material';
import TagManager from 'react-gtm-module';

import MenuBar from './components/MenuBar';
import Footer from './components/Footer';
import CookieConsent from "react-cookie-consent";
import './App.css';
import favIcon from './assets/images/fav-icon.png';
import { track, pageLocation, tipoPaginaSite } from './utils/track';

// Lazy loading das páginas para performance (Code Splitting)
const Home = lazy(() => import('./pages/Home'));
const Artistas = lazy(() => import('./pages/Artistas'));
const Perfil = lazy(() => import('./pages/Perfil'));
const QuemSomos = lazy(() => import('./pages/QuemSomos'));
const CityLanding = lazy(() => import('./pages/CityLanding'));
const DJLanding = lazy(() => import('./pages/DJLanding'));
const LeadSuccess = lazy(() => import('./pages/LeadSuccess'));
const TrackOrder = lazy(() => import('./pages/TrackOrder'));
const SuporteCliente = lazy(() => import('./pages/SuporteCliente'));
const PoliticaPrivacidade = lazy(() => import('./pages/PoliticaPrivacidade'));
const TermosUso = lazy(() => import('./pages/TermosUso'));
const NotFound = lazy(() => import('./pages/NotFound'));

const gtmId = 'GTM-WPGB5JHJ';

function MainContent() {
  const location = useLocation();
  const isSmallScreen = useMediaQuery('(max-width:400px)');

  // Reseta o scroll para o topo sempre que a rota mudar
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // page_view manual (inclusive no 1º carregamento). As páginas são lazy e o <title>
  // vem do Helmet depois do render, então espera o título mudar (ou 800ms) para não
  // mandar o título da página anterior.
  useEffect(() => {
    const { pathname } = location;
    const loc = pageLocation(window.location);
    const previousTitle = document.title;
    let sent = false;

    const send = () => {
      if (sent) return;
      sent = true;
      observer.disconnect();
      clearTimeout(timer);
      track('page_view', {
        page_location: loc,
        page_title: document.title,
        tipo_pagina: tipoPaginaSite(pathname),
      });
    };

    const observer = new MutationObserver(() => {
      if (document.title !== previousTitle) send();
    });
    observer.observe(document.head, { subtree: true, childList: true, characterData: true });
    const timer = setTimeout(send, 800);

    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [location]);

  // Verifica se o usuário já deu consentimento anteriormente para ativar o GTM
  useEffect(() => {
    const cookieName = "djparafestas_lgpd_consent";
    const hasConsent = document.cookie.split('; ').find(row => row.startsWith(`${cookieName}=true`));
    
    if (hasConsent) {
      TagManager.initialize({ gtmId });
    }
  }, []);

  return (
    <div className="App">
      {location.pathname !== '/' && <MenuBar/>}
      <Suspense fallback={
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      }>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/artistas" element={<Artistas />} />
        <Route path="/perfil/:artistName" element={<Perfil />} />
        <Route path="/quem-somos" element={<QuemSomos />} />
        <Route path="/djs/:citySlug" element={<CityLanding />} />
        <Route path="/sou-dj" element={<DJLanding />} />
        <Route path="/suporte" element={<SuporteCliente />} />
        <Route path="/politica-privacidade" element={<PoliticaPrivacidade />} />
        <Route path="/termos-uso" element={<TermosUso />} />
        
        {/* Rotas para a página de sucesso/acompanhamento da cotação */}
        <Route path="/proposta/:id" element={<LeadSuccess />} />
        <Route path="/leads/:id" element={<LeadSuccess />} />
        
        {/* Rota de Acompanhamento com Autenticação via Token/Assinatura */}
        <Route path="/track-order/:uuid" element={<TrackOrder />} />
        
        {/* Página 404 - Rota Curinga para URLs inexistentes */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      <Footer/>
      <CookieConsent
        location="bottom"
        buttonText="Entendi e Aceito"
        cookieName="djparafestas_lgpd_consent"
        style={{ background: "#2E0249", zIndex: 9999, borderTop: "1px solid rgba(255,255,255,0.1)", display: "flex", justifyContent: "center" }}
        buttonStyle={{ 
          background: "linear-gradient(90deg, #00C6FF 0%, #0072FF 100%)", 
          color: "#fff", 
          fontSize: isSmallScreen ? "12px" : "14px", 
          fontWeight: "bold",
          borderRadius: "8px",
          padding: isSmallScreen ? "10px 10px" : "10px 24px"
        }}
        enableDeclineButton
        declineButtonText="Apenas Necessários"
        declineButtonStyle={{
          background: "transparent",
          border: "1px solid rgba(255,255,255,0.5)",
          color: "#fff",
          fontSize: isSmallScreen ? "12px" : "14px", 
          fontWeight: "bold",
          borderRadius: "8px",
          padding: isSmallScreen ? "10px 10px" : "10px 24px",
          marginLeft: isSmallScreen ? "5px" : "10px"
        }}
        expires={365}
        onAccept={() => {
          TagManager.initialize({ gtmId });
        }}
      >
        Este site utiliza cookies para garantir uma melhor experiência. Ao continuar navegando, você concorda com nossa <Link to="/politica-privacidade/" style={{ color: "#00C6FF", textDecoration: "none", fontWeight: "bold" }}>Política de Privacidade</Link> e os <Link to="/termos-uso/" style={{ color: "#00C6FF", textDecoration: "none", fontWeight: "bold" }}>Termos de Uso</Link>.
      </CookieConsent>
    </div>
  );
}

function App() {
  useEffect(() => {
    const link = document.querySelector("link[rel~='icon']");
    if (link) {
      link.href = favIcon;
    } else {
      const newLink = document.createElement('link');
      newLink.rel = 'icon';
      newLink.href = favIcon;
      document.head.appendChild(newLink);
    }
  }, []);

  return (
    <Router>
      <MainContent />
    </Router>
  );
}

export default App;
